import { Box } from '@mui/material'
import { ImageUrl } from '~/generated/graphql'
import React, { useMemo } from 'react'
import { CampaignCoverImage } from '~/lib/types/Campaign'

const CampaignImageSize = {
  Original: 'original',
  Small: 'small',
  Normal: 'normal',
} as const

type CampaignImageSize = (typeof CampaignImageSize)[keyof typeof CampaignImageSize]

type Props = {
  image: CampaignCoverImage
  imageSize?: CampaignImageSize
  borderWidth?: number
  variant?: 'primary' | 'error' | 'noBorder'
}

type Props_ = {
  image: ImageUrl
  imageSize?: CampaignImageSize
  borderWidth?: number
  variant?: 'primary' | 'error'
}

const CampaignImage = (props: Props) => {
  const imageUrl = useMemo(() => {
    // 何も指定されなかったらNormalで
    const size = props.imageSize ?? CampaignImageSize.Normal

    switch (size) {
      case CampaignImageSize.Original:
        return props.image.url
      case CampaignImageSize.Small:
        // Note: リサイズ後の画像は必ず存在するとは限らない
        return props.image.w120xh120?.url ?? props.image.url
      case CampaignImageSize.Normal:
        // Note: リサイズ後の画像は必ず存在するとは限らない
        return props.image.w480xh480?.url ?? props.image.url
      default:
        return props.image.url
    }
  }, [props.image, props.imageSize])

  // キャンペーン画像用のプレースホルダーは6種類あるので、いい感じに出し分ける
  // とりあえずはキャンペーン画像URLのハッシュコードを6で割る
  const placeholderUrl = useMemo(() => {
    const str = props.image.url
    let hashcode = 0
    for (let i = 0; i < str.length; i++) {
      hashcode = (Math.imul(31, hashcode) + str.charCodeAt(i)) | 0
    }

    const index = (Math.abs(hashcode) % 6) + 1
    return `/campaign_placeholder/placeholder_${index}.jpg`
  }, [props.image])

  return (
    <Box
      position="relative" //プレースホルダーとキャンペーン画像を重ねるため
      borderRadius="4px"
      overflow="hidden" //親の角丸で子供を切り取るため
      sx={{ paddingTop: 'calc(100%)' }}
    >
      {
        // プレースホルダー
      }
      <img
        src={placeholderUrl}
        alt=""
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          objectFit: 'cover',
        }}
      />

      {
        // キャンペーン画像
      }
      <img
        src={imageUrl}
        alt=""
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          objectFit: 'cover',
        }}
      />

      {
        // 枠線描画用のBox
        // Note: 設定を親のBoxに移すと、描画サイズによって枠線と画像の間に隙間が空いてしまうことがあった
      }
      <Box
        position="absolute"
        borderRadius="4px"
        left={0}
        top={0}
        bottom={0}
        right={0}
        border={props.variant === 'noBorder' ? 0 : 1}
        borderColor={
          props.variant === 'noBorder' ? 'transparent' : props.variant === 'error' ? 'error.main' : 'primary.main'
        }
        style={{
          borderWidth: props.borderWidth ?? 2,
        }}
      />
    </Box>
  )
}

// toridori-marketing-apiに完全に移行したら、こっちに切り替える
export const CampaignImage_ = (props: Props_) => {
  const imageUrl = useMemo(() => {
    // 何も指定されなかったらNormalで
    const size = props.imageSize ?? CampaignImageSize.Normal

    switch (size) {
      case CampaignImageSize.Original:
        return props.image.original
      case CampaignImageSize.Small:
        // Note: リサイズ後の画像は必ず存在するとは限らない
        return props.image.small
      case CampaignImageSize.Normal:
        // Note: リサイズ後の画像は必ず存在するとは限らない
        return props.image.medium
      default:
        return props.image.original
    }
  }, [props.image, props.imageSize])

  // キャンペーン画像用のプレースホルダーは6種類あるので、いい感じに出し分ける
  // とりあえずはキャンペーン画像URLのハッシュコードを6で割る
  const placeholderUrl = useMemo(() => {
    const str = props.image.original
    let hashcode = 0
    for (let i = 0; i < str.length; i++) {
      hashcode = (Math.imul(31, hashcode) + str.charCodeAt(i)) | 0
    }

    const index = (Math.abs(hashcode) % 6) + 1
    return `/campaign_placeholder/placeholder_${index}.jpg`
  }, [props.image])

  return (
    <Box
      position="relative" //プレースホルダーとキャンペーン画像を重ねるため
      borderRadius="4px"
      overflow="hidden" //親の角丸で子供を切り取るため
      sx={{ paddingTop: 'calc(100%)' }}
    >
      {
        // プレースホルダー
      }
      <img
        src={placeholderUrl}
        alt=""
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          objectFit: 'cover',
        }}
      />

      {
        // キャンペーン画像
      }
      <img
        src={imageUrl}
        alt=""
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          objectFit: 'cover',
        }}
      />

      {
        // 枠線描画用のBox
        // Note: 設定を親のBoxに移すと、描画サイズによって枠線と画像の間に隙間が空いてしまうことがあった
      }
      <Box
        position="absolute"
        borderRadius="4px"
        left={0}
        top={0}
        bottom={0}
        right={0}
        border={1}
        borderColor={props.variant === 'error' ? 'error.main' : 'primary.main'}
        style={{
          borderWidth: props.borderWidth ?? 2,
        }}
      />
    </Box>
  )
}

export default CampaignImage
